import DialogCloseButton from '@components/common/dialog/DialogCloseButton';
import MaterialIcon from '@components/common/icons/MaterialIcon';
import { Column, FlexColumn, Row } from '@components/common/layoutUtils';
import TippyTooltip from '@components/common/tooltip/TippyTooltip';
import colors from '@utils/colors';
import { largeModalStyle, popupEdgeCSS } from '@utils/popupUtils';
import storage from '@utils/storage';
import { LargeText, MediumText, RegularText } from '@utils/typography';
import { useEffect, useMemo, useState } from 'react';
import Popup from 'reactjs-popup';
import styled from 'styled-components';
import CourseDetailsActionImage from '@assets/images/latest-updates/course-details-action.png';
import CourseDetailsImage from '@assets/images/latest-updates/course-details.png';
import CheckForLmsUpdates from '@assets/images/latest-updates/check-for-lms-updates.png';
import StudySteakOverviewImage from '@assets/images/latest-updates/study-streak-overview.png';
import StudySteakImage from '@assets/images/latest-updates/study-streak.png';
import ShareCalendarActionImage from '@assets/images/latest-updates/share-calendar-action.png';
import ShareCalendarImage from '@assets/images/latest-updates/share-calendar.png';
import MobileAppImage from '@assets/images/latest-updates/mobile-app.png';
import ImportSyllabusImage from '@assets/images/latest-updates/import-syllabus.png';
import TaskEstimationsHintImage from '@assets/images/latest-updates/task-estimations-hint.png';
import TaskEstimationsDialogImage from '@assets/images/latest-updates/task-estimations-dialog.png';
import TaskEstimatorOverviewImage from '@assets/images/latest-updates/task-estimator-overview.png';
import NewWidgetImage from '@assets/images/latest-updates/new-widget.png';
import WeekBreakdownImage from '@assets/images/latest-updates/week-breakdown.png';
import TimerCountdownImage from '@assets/images/latest-updates/timer-countdown.png';
import TimerTimesUpImage from '@assets/images/latest-updates/timer-times-up.png';
import BumpersMenuImage from '@assets/images/latest-updates/bumpers-menu.png';
import AutoPlanButtonImage from '@assets/images/latest-updates/autoplan-button.png';
import AutoPlanWeekImage from '@assets/images/latest-updates/autoplan-week.png';
import TaskListImage from '@assets/images/latest-updates/task-list.png';
import UndoImage from '@assets/images/latest-updates/undo.png';
import CategoryTimeImage from '@assets/images/latest-updates/category-time.png';
import ThemeSoftWhiteImage from '@assets/images/latest-updates/theme-soft-white.png';
import ThemeCreamImage from '@assets/images/latest-updates/theme-cream.png';
import ThemeInkImage from '@assets/images/latest-updates/theme-ink.png';
import ThemeNavyImage from '@assets/images/latest-updates/theme-navy.png';
import { usePreviewAccess } from '@hooks/usePreviewAccess';
import BumpersDemo from './BumpersDemo';

const THEME_PICTURES = [
  { name: 'Soft white', image: ThemeSoftWhiteImage },
  { name: 'Cream', image: ThemeCreamImage },
  { name: 'Ink', image: ThemeInkImage },
  { name: 'Navy', image: ThemeNavyImage }
];

const updates = [
  {
    id: 18,
    title: 'New Study Timer: Press Play and Just Study',
    content: (
      <Row>
        <Column>
          <p style={{ whiteSpace: 'pre-line', margin: 0, lineHeight: 1.5 }}>
            Stop watching the clock. Press play on a session, and Shovel counts down the time you planned, tells you when
            it&apos;s up, and logs it in one click.
          </p>
          <RegularText>Full Update Details:</RegularText>
          <ul style={{ margin: 0 }}>
            <li>Press ▶ right on a session on your calendar</li>
            <li>Choose Countdown or Stopwatch, and Shovel remembers your pick</li>
            <li>The timer stays in your top bar: see what&apos;s left, pause it, or ✓ to log your time</li>
            <li>Time&apos;s up? A soft chime lets you know, and you can turn on a desktop notice too</li>
            <li>Starting late? The rest of your day moves down to make room. Classes stay put, and Undo puts it all back</li>
            <li>
              Running past your plan? It asks if you&apos;re still studying, and pauses itself if you&apos;ve wandered off
            </li>
          </ul>
        </Column>
        <Column style={{ gap: 12, flex: 'none' }}>
          <img
            src={TimerCountdownImage}
            alt="The timer in the top bar and its popup, counting down"
            style={{ width: '320px' }}
          />
          <img src={TimerTimesUpImage} alt="The timer when time is up: Keep going or Log" style={{ width: '320px' }} />
        </Column>
      </Row>
    ),
    date: Date.parse('2026-11-03')
  },
  {
    id: 17,
    title: 'Bumpers: Move One Session and the Rest Make Room',
    content: (
      <Row>
        <Column>
          <p style={{ whiteSpace: 'pre-line', margin: 0, lineHeight: 1.5 }}>
            Plans change. Drag a session to a new spot and everything in its way slides along, like blocks on a timeline. No
            more rebuilding your whole week because one thing moved.
          </p>
          <RegularText>Full Update Details:</RegularText>
          <ul style={{ margin: 0 }}>
            <li>The push carries on across days until it reaches a free gap</li>
            <li>Classes and events stay put, and sessions flow around them</li>
            <li>A session won&apos;t get pushed past its due date</li>
            <li>
              Pick how passing works: with Jump, the sessions you pass stay where they are. With Bump, they move along. Hold
              ⇧ while dragging to switch for that one drag
            </li>
            <li>Want a spot no matter what? Hold ⇧ while dragging a task from your sidebar, and it drops anywhere</li>
            <li>Turn it on or off with the Bumpers switch above your calendar</li>
          </ul>
        </Column>
        <Column style={{ gap: 14, flex: 'none' }}>
          <BumpersDemo />
          <img src={BumpersMenuImage} alt="The Bumpers switch with its Jump and Bump choices" style={{ width: '300px' }} />
        </Column>
      </Row>
    ),
    date: Date.parse('2026-11-03')
  },
  {
    id: 16,
    title: 'Smaller Upgrades You Might Notice',
    content: (
      <ul style={{ margin: 0 }}>
        <li>Moving between weeks now glides, so you can tell the page changed</li>
        <li>“Mark as used” is now “Log time”: same button, clearer name</li>
        <li>Fewer pings: the overdue and morning reminders are gone, and session pings say what&apos;s next and how long</li>
        <li>Sessions that overlap now sit side by side, so nothing hides behind anything else</li>
        <li>Event details keep the line breaks you type</li>
      </ul>
    ),
    date: Date.parse('2026-11-03')
  },
  {
    id: 15,
    title: 'Auto-plan: Your Week Planned in One Click',
    content: (
      <Row>
        <Column>
          <p style={{ whiteSpace: 'pre-line', margin: 0, lineHeight: 1.5 }}>
            Stop staring at an empty calendar. Press Auto-plan and Shovel fits your tasks around your classes, activities and
            sleep, before they&apos;re due.
          </p>
          <img src={AutoPlanButtonImage} alt="The Auto-plan button" style={{ width: '111px' }} />
          <RegularText>Full Update Details:</RegularText>
          <ul style={{ margin: 0 }}>
            <li>Tell it how you study: session length, breaks, daily limit, favorite times and days off</li>
            <li>See what fits before anything lands on your calendar</li>
            <li>Missed a session or fell behind? Replan picks it back up</li>
            <li>Add review sessions after your readings, if you want them</li>
            <li>Don&apos;t like the plan? Undo or Unplan takes it off in one click</li>
          </ul>
        </Column>
        <img
          src={AutoPlanWeekImage}
          alt="A week with study sessions planned around classes"
          style={{ width: '320px', flex: 'none', borderRadius: '8px' }}
        />
      </Row>
    ),
    date: Date.parse('2026-10-10')
  },
  {
    id: 14,
    title: 'The Task List, Rebuilt',
    content: (
      <Column style={{ gap: 12 }}>
        <p style={{ whiteSpace: 'pre-line', margin: 0, lineHeight: 1.5 }}>
          See everything you owe in one clean list, and fix a dozen tasks in one go instead of one by one.
        </p>
        <RegularText>Full Update Details:</RegularText>
        <ul style={{ margin: 0 }}>
          <li>Tick a few tasks, then change their due date, time, category and more at once. Complete or trash them too</li>
          <li>Click a name to open the task beside the list, or rename it right where it is</li>
          <li>Group by due date, category or reading source, or sort by hand</li>
          <li>Add columns for Estimated, Logged and Planned time</li>
        </ul>
        <img
          src={TaskListImage}
          alt="The Task List with three tasks selected"
          style={{ width: '100%', borderRadius: '8px' }}
        />
      </Column>
    ),
    date: Date.parse('2026-10-24')
  },
  {
    id: 13,
    title: 'Undo, for Almost Everything',
    content: (
      <Row>
        <Column>
          <p style={{ whiteSpace: 'pre-line', margin: 0, lineHeight: 1.5 }}>
            Moved the wrong thing? Deleted something by accident? Press ⌘Z (Ctrl+Z on Windows) or the undo arrow on your
            calendar, and it&apos;s back.
          </p>
          <RegularText>Full Update Details:</RegularText>
          <ul style={{ margin: 0 }}>
            <li>Go back up to 20 steps</li>
            <li>Works for moving sessions, planning and unplanning, and logging time</li>
            <li>Also for completing or trashing tasks, and for deleting and editing events</li>
          </ul>
        </Column>
        <img src={UndoImage} alt="The Undo message and the undo arrow" style={{ width: '380px', flex: 'none' }} />
      </Row>
    ),
    date: Date.parse('2026-10-25')
  },
  {
    id: 12,
    title: 'Estimates That Get Smarter as You Go',
    content: (
      <Row>
        <Column>
          <p style={{ whiteSpace: 'pre-line', margin: 0, lineHeight: 1.5 }}>
            You asked for this one. Give a category a default time once, and every new task in it starts there. If a task
            takes much longer or shorter than you thought, Shovel offers to update the rest.
          </p>
          <RegularText>Full Update Details:</RegularText>
          <ul style={{ margin: 0 }}>
            <li>Set each category&apos;s default time in Course Details</li>
            <li>Shovel asks when a finished task was 20% or more off its estimate</li>
            <li>Readings work the same way, using your reading pace</li>
          </ul>
        </Column>
        <img
          src={CategoryTimeImage}
          alt="Shovel asking whether to use a new time for the rest of a category"
          style={{ width: '300px', flex: 'none' }}
        />
      </Row>
    ),
    date: Date.parse('2026-10-31')
  },
  {
    id: 11,
    title: 'Make Shovel Yours: New Backgrounds and Dark Modes',
    content: (
      <Column style={{ gap: 12 }}>
        <p style={{ whiteSpace: 'pre-line', margin: 0, lineHeight: 1.5 }}>
          You spend a lot of time in your planner, so it should be easy on the eyes. Find all of this in Settings →
          Personalization.
        </p>
        <RegularText>Full Update Details:</RegularText>
        <ul style={{ margin: 0 }}>
          <li>Seven light backgrounds, from Soft white to Cream and Blush</li>
          <li>Seven dark modes, including Ink, Onyx and Navy</li>
          <li>Pick your own accent color, and a separate one for free study time</li>
        </ul>
        <Row style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
          {THEME_PICTURES.map(({ name, image }) => (
            <Column key={name} style={{ width: '172px', gap: 6, alignItems: 'center' }}>
              <img src={image} alt={`The calendar in ${name}`} style={{ width: '100%', borderRadius: '8px' }} />
              {name}
            </Column>
          ))}
        </Row>
      </Column>
    ),
    date: Date.parse('2026-10-11')
  },
  {
    id: 10,
    title: 'Week breakdown on the calendar',
    content: (
      <Row>
        <Column>
          <p style={{ whiteSpace: 'pre-line', margin: 0, lineHeight: 1.5 }}>
            See how the week on your calendar is spent, without leaving the planner. Click the small chart next to your study
            streak.
          </p>
          <RegularText>Full Update Details:</RegularText>
          <ul style={{ margin: 0 }}>
            <li>See sleep, class, activities, events, logged study time, planned study time, and free study time</li>
            <li>Expand class time and activities to see each course or activity</li>
            <li>The chart follows the dates you are looking at</li>
          </ul>
        </Column>
        <img src={WeekBreakdownImage} style={{ width: '260px' }} />
      </Row>
    ),
    date: Date.parse('2026-09-20')
  },
  {
    id: 9,
    title: 'Widget for iOS and Android',
    content: (
      <Row>
        <Column>
          <p style={{ whiteSpace: 'pre-line', margin: 0, lineHeight: 1.5 }}>
            {`See what's next without even opening the app. Add the Shovel widget to your home screen for a view of your day.`}
          </p>
          <RegularText>Full Update Details:</RegularText>
          <ul style={{ margin: 0 }}>
            <li>See your upcoming events and planned tasks right from your home screen</li>
            <li>Check how many tasks are due today</li>
            <li>Stay on top of your schedule without unlocking the app</li>
            <li>Available for both iOS and Android</li>
          </ul>
        </Column>
        <img src={NewWidgetImage} style={{ width: '300px' }} />
      </Row>
    ),
    date: Date.parse('2026-08-23')
  },
  {
    id: 8,
    title: 'Task Estimator: Faster and More Flexible',
    content: (
      <Row>
        <Column>
          <p style={{ whiteSpace: 'pre-line', margin: 0, lineHeight: 1.5 }}>
            {`The Task Estimator got a big usability upgrade — quicker to use, with more ways to estimate exactly how you like.`}
          </p>
          <RegularText>Full Update Details:</RegularText>
          <ul style={{ margin: 0 }}>
            <li>Estimate a whole category at once with one default, or fine-tune tasks individually</li>
            <li>Set pages instead of time for reading tasks, plus your own reading speed for accurate estimates</li>
            <li>Categorize or recategorize tasks in seconds, right from the estimator</li>
          </ul>
        </Column>
        <img src={TaskEstimatorOverviewImage} style={{ width: '300px' }} />
      </Row>
    ),
    date: Date.parse('2026-08-18')
  },
  {
    id: 7,
    title: 'Task Estimations by Category + AI Task Categorization',
    content: (
      <Row>
        <Column>
          <p style={{ whiteSpace: 'pre-line', margin: 0, lineHeight: 1.5 }}>
            {`Estimate your tasks faster by setting time per category instead of one by one. 
This allows you to quickly define how long typical tasks take and apply it across your entire workload.

You can also let AI automatically categorize your tasks, so everything is organized and ready for planning in seconds — no manual setup needed.

When you have more than 10 tasks without estimates, you’ll see a suggestion to quickly estimate them by category and unlock more accurate planning.`}
          </p>
          {/* <a
            target="_blank"
            href="https://courses.shovelapp.io/lessons/pdf-syllabus-upload/"
            style={{ color: '#786FFF', textDecoration: 'none' }}
          >
            Learn more
          </a> */}
        </Column>
        <Column style={{ gap: 0 }}>
          <img src={TaskEstimationsHintImage} style={{ width: '180px' }} />
          <img src={TaskEstimationsDialogImage} style={{ width: '280px' }} />
        </Column>
      </Row>
    ),
    date: Date.parse('2026-03-30')
  },
  {
    id: 6,
    title: 'AI Syllabus Upload!',
    content: (
      <Row>
        <Column>
          <p style={{ whiteSpace: 'pre-line', margin: 0, lineHeight: 1.5 }}>
            {`Say goodbye to manual setup 👋
You can now upload a PDF syllabus into Shovel, and our AI will automatically create all your tasks and events for you. Classes, deadlines, readings—done in seconds.
How it works:
Go to Course Details → Upload Syllabus → Relax 😌
Less setup. More getting things done.
Your semester just got way easier. 💪📚`}
          </p>
          <a
            target="_blank"
            href="https://courses.shovelapp.io/lessons/pdf-syllabus-upload/"
            style={{ color: '#786FFF', textDecoration: 'none' }}
          >
            Learn more
          </a>
        </Column>
        <img src={ImportSyllabusImage} style={{ width: '300px' }} />
      </Row>
    ),
    date: Date.parse('2026-01-29')
  },
  {
    id: 5,
    title: 'New Course Details Dialog',
    content: (
      <Row>
        <Column>
          <div style={{ whiteSpace: 'pre-line', margin: 0, lineHeight: 1.5 }}>
            There is a new dialog for course information. It provides a centralized place to manage and view all details
            related to a specific course, making it easier to stay organized and keep track of your academic progress. Inside
            the dialog, you can now:
            <ul>
              <li>Sync with Google Calendar or educational platforms (Canvas, Brightspace, Moodle or others)</li>
              <li>Import course syllabus from a file</li>
              <li>Export course syllabus and share with friends</li>
              <li>Share course calendar</li>
              <li>Jump to course related tasks or grades</li>
              <li>View and edit course information - task categories, reading sources, teachers, and notes</li>
            </ul>
            Grades have been moved from course information into a separate dialog.
          </div>
        </Column>
        <Column style={{ gap: 20 }}>
          <img src={CourseDetailsActionImage} style={{ width: '120px' }} />
          <img src={CourseDetailsImage} style={{ width: '300px' }} />
        </Column>
      </Row>
    ),
    date: Date.parse('2025-12-16')
  },
  {
    id: 4,
    title: 'Sync LMS Anytime',
    content: (
      <Row>
        <Column>
          <p style={{ whiteSpace: 'pre-line', margin: 0, lineHeight: 1.5 }}>
            If you’ve connected your LMS (Canvas, Brightspace, Moodle, or others) to Shovel, you can now check for updates at
            any time. Trigger a manual sync to ensure Shovel always reflects the latest data from your LMS. Shovel still
            syncs your LMS automatically every day, but now you’re in control too.
          </p>
        </Column>
        <img src={CheckForLmsUpdates} style={{ width: '180px' }} />
      </Row>
    ),
    date: Date.parse('2025-12-16')
  },
  {
    id: 3,
    title: 'Mobile App Updates (version 3.2.0)',
    content: (
      <Row>
        <Column style={{ gap: 10 }}>
          <p style={{ whiteSpace: 'pre-line', margin: 0, lineHeight: 1.5 }}>
            {`🚀 New in Shovel!
          Your favorite study planner just got faster, smoother, and smarter.
          ✨ Improved calendar performance, new 2-day view, study streak tracking, and more ways to plan your tasks and events.
          💪 Smarter. Faster. More Shovel.`}
          </p>
          <RegularText>Full Update Details:</RegularText>
          <ul style={{ margin: 0 }}>
            <li>Improved Calendar Performance — faster, smoother, better.</li>
            <li>Faster Loading — less waiting, more studying (or Netflix).</li>
            <li>Swipe Between Days — cruise through your schedule like a pro.</li>
            <li>Go to Today — never lose track again.</li>
            <li>2-Day View — see what’s coming without feeling overwhelmed.</li>
            <li>Study Streak Tracking — keep your momentum going!</li>
            <li>Improved Navigation — find everything easier.</li>
            <li>More Event Repeat Options — because class does happen every week 😅</li>
            <li>Create Courses & Activities — get organized your way.</li>
            <li>Improved Task & Event Creation — faster setup, fewer taps.</li>
            <li>Option to Remove Completed Tasks from the Top Bar — for that clean, satisfying look.</li>
          </ul>
        </Column>
        <img src={MobileAppImage} style={{ width: '170px' }} />
      </Row>
    ),
    date: Date.parse('2025-11-15')
  },
  {
    id: 2,
    title: 'Share Full Calendar',
    content: (
      <Row>
        <Column>
          You can now share your complete calendar and link it with your Google, Apple, or Outlook calendar. You can choose
          between sharing events, planned tasks, or both.
        </Column>
        <Column style={{ gap: 20 }}>
          <img src={ShareCalendarActionImage} style={{ width: '120px' }} />
          <img src={ShareCalendarImage} style={{ width: '350px' }} />
        </Column>
      </Row>
    ),
    date: Date.parse('2025-09-20')
  },
  {
    id: 1,
    title: 'Study Streak Feature',
    content: (
      <Row>
        <Column>
          The study streak feature has been added to help you stay motivated and consistent with your study habits. You can
          track your daily study streaks and compare your progress to the previous day or week. Celebrate your achievements
          and keep pushing forward!
        </Column>
        <Column style={{ gap: 20 }}>
          <img src={StudySteakImage} style={{ width: '120px' }} />
          <img src={StudySteakOverviewImage} style={{ width: '350px' }} />
        </Column>
      </Row>
    ),
    date: Date.parse('2025-09-05')
  }
];

const previewUpdates = [];

const WhatsNew = () => {
  const [open, setOpen] = useState(false);
  const [hasNewUpdates, setHasNewUpdates] = useState(false);

  const hasPreviewAccess = usePreviewAccess();

  const latestUpdateId = (hasPreviewAccess ? [...previewUpdates, ...updates] : updates)[0]?.id || 0;

  useEffect(() => {
    const latestUpdateIdSeen = storage.getWhatsNewLastSeen();
    if (latestUpdateId > latestUpdateIdSeen) {
      setHasNewUpdates(true);
    }
  }, []);

  return (
    <>
      <TippyTooltip
        target={
          <Item
            onClick={() => {
              setOpen(true);
              if (hasNewUpdates) {
                storage.setWhatsNewLastSeen(latestUpdateId);
                setHasNewUpdates(false);
              }
            }}
          >
            <MaterialIcon className={'navigation-icon'} name={'campaign'} size={20} />
            {hasNewUpdates && <NewUpdatesIndicator />}
          </Item>
        }
        position={'right'}
        content={"What's New"}
      />
      <Dialog
        open={open}
        contentStyle={{ ...largeModalStyle, position: 'relative' }}
        overlayStyle={{ overflow: 'scroll' }}
        lockScroll={false}
        closeOnEscape={true}
        closeOnDocumentClick={true}
        onClose={() => {
          setOpen(false);
        }}
      >
        <>
          <DialogCloseButton onClick={() => setOpen(false)} />
          <WhatsNewContent />
        </>
      </Dialog>
    </>
  );
};

export default WhatsNew;

type WhatsNewContentProps = {};

export const WhatsNewContent: React.FC<WhatsNewContentProps> = ({}) => {
  const hasPreviewAccess = usePreviewAccess();

  const groups = useMemo(() => {
    // sort updates by date desc (most recent month first), stable for same dates
    const byTimeDesc = [...(hasPreviewAccess ? [...previewUpdates, ...updates] : updates)].sort((a, b) => {
      if (b.date !== a.date) return b.date - a.date;
      return b.id - a.id;
    });

    const map = new Map<string, { label: string; dateKey: number; items: typeof updates }>();

    byTimeDesc.forEach(u => {
      const d = new Date(u.date);
      const label = d.toLocaleString(undefined, { month: 'long', year: 'numeric' });
      const dateKey = d.getFullYear() * 100 + (d.getMonth() + 1); // numeric key for ordering if needed
      if (!map.has(label)) map.set(label, { label, dateKey, items: [] });
      map.get(label)!.items.push(u);
    });

    // convert to array and ensure month groups are sorted by month (desc)
    return Array.from(map.values()).sort((a, b) => b.dateKey - a.dateKey);
  }, [hasPreviewAccess]);

  return (
    <Wrapper>
      <Header>
        <Title>
          <LargeText strong>Latest Updates</LargeText>
        </Title>
      </Header>

      <Content>
        {groups.map(group => (
          <div key={group.label}>
            <MonthHeader>{group.label}</MonthHeader>
            {group.items
              .slice()
              .sort((a, b) => b.id - a.id) // render items in each month by id desc
              .map(u => (
                <UpdateEntry key={u.id}>
                  <UpdateTitle>{u.title}</UpdateTitle>
                  <UpdateContent>{u.content}</UpdateContent>
                </UpdateEntry>
              ))}
          </div>
        ))}
      </Content>
    </Wrapper>
  );
};

const Dialog = styled(Popup)`
  &-content {
    background: ${({ theme }) => theme.background} !important;
    ${popupEdgeCSS};
  }

  &-overlay {
    background: ${({ theme }) => theme.modalOverlayColor} !important;
  }
`;

const Wrapper = styled(Column)`
  width: 100%;
  padding: 20px 10px 0 10px;
  gap: 10px;
  overflow: scroll;
`;

const Title = styled(Row)`
  justify-content: center;
  white-space: pre;
`;

const Content = styled(FlexColumn)`
  display: flex;
  box-sizing: border-box;
  height: 100%;
  min-height: 0;
  padding: 0 8px;
`;

const Header = styled.div`
  margin-bottom: 10px;
  text-align: center;
`;

const Item = styled.div`
  margin-bottom: 10px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  text-decoration: none;
  box-sizing: border-box;
  border-radius: 5px;
  padding: 5px;
  background-color: inherit;
  position: relative;

  &:hover {
    background: ${({ theme }) => theme.backgroundDark};

    .navigation-icon {
      color: ${({ theme }) => theme.primary};
    }
  }
`;

const NewUpdatesIndicator = styled.div`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  background-color: ${colors.negative};
  border-radius: 50%;
`;

const MonthHeader = styled(MediumText)`
  display: block;
  margin-top: 10px;
  margin-bottom: 6px;
  text-transform: none;
  color: ${props => props.theme.primaryDark};
`;

const UpdateEntry = styled.div`
  padding: 10px;
  border-radius: 6px;
  background: transparent;
`;

const UpdateTitle = styled(RegularText)`
  font-weight: bold;
  margin-bottom: 6px;
  color: ${props => props.theme.textStrongColor};
  margin-bottom: 10px;
  display: block;
`;

const UpdateContent = styled.div`
  font-family: 'Quicksand', sans-serif;
  font-style: normal;
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.theme.textColor};
  word-break: break-word;
  line-height: 1.5;
  overflow: hidden;

  & > ${Row} {
    align-items: flex-start;
    gap: 16px;
  }
`;
